import React, { useContext, useEffect, useState, useCallback } from 'react';
import { AlertMessagesContext } from 'react-alert-messages';
import { Grid, Skeleton } from '@mui/material';
import OnlineOrderService from 'services/OnlineOrderService';
import styles from './styles.module.css';
import DateTimeHelpers from 'helpers/DateTimeHelpers';
import CloseIcon from '@mui/icons-material/Close';
import ShopsHelper from 'helpers/ShopsHelper';
import { ShopContext } from 'Context/ShopContext';
import { useTranslation } from 'react-i18next';
import Info from 'components/utils/Alert/Info';
import DataTable from 'components/utils/DataTable';

export default function OnlinePaymentDetails({ payment, onClose }) {
  const { t: tCommon } = useTranslation('common');
  const { t: tOperations } = useTranslation('operations');
  const { postAlertMessage } = useContext(AlertMessagesContext);
  const { shop } = useContext(ShopContext);

  const [paymentDetail, setPaymentDetail] = useState(payment || null);
  const [loading, setLoading] = useState(!payment);

  const fetchPaymentDetails = useCallback(async () => {
    if (!payment?.id) return;
    setLoading(true);
    try {
      const data = await OnlineOrderService.getCustomerPaymentDetails(payment.id);
      setPaymentDetail(data);
    } catch (err) {
      postAlertMessage({ text: err.message, type: 'failed' });
    } finally {
      setLoading(false);
    }
  }, [payment, postAlertMessage]);

  useEffect(() => {
    if (!paymentDetail && payment?.id) {
      setLoading(true);
      fetchPaymentDetails();
    }
  }, [paymentDetail, payment, fetchPaymentDetails]);

  if (loading && !paymentDetail) {
    return (
      <div className={styles.popUp}>
        <div className={styles.contentWrapperr}>
          <Skeleton variant="rectangular" height={50} animation="wave" />
          <Skeleton variant="rectangular" height={200} animation="wave" style={{ marginTop: 20 }} />
        </div>
      </div>
    );
  }
  if (!paymentDetail) {
    return <Info title="Error" content={tOperations('Payment details not load')} />;
  }

  const pd = paymentDetail.data ? paymentDetail.data : paymentDetail;
  const customerObj = pd.action_data?.customer;
  const items = pd.action_data?.items || [];

  const itemColumns = [
    { label: tCommon('Product'), id: 'name', type: 'text' },
    { label: tCommon('Qty'), id: 'quantity', type: 'text' },
    { label: tCommon('Rate'), id: 'rate', type: 'text' },
    {
      label: tCommon('Total'),
      id: 'total',
      type: 'callback',
      viewRender: (r) => ShopsHelper.getAmountFormatted(shop, r.quantity * r.rate),
    },
  ];

  return (
    <div className={styles.popUp}>
      <div className={styles.contentWrapperr}>
        <div className={styles.titleSec}>
          <h2 className={styles.title}>
            {tCommon('Payment Details')}
            <span className={styles.menuTitle}>#{pd.id}</span>
          </h2>
          <div>
            <CloseIcon onClick={onClose} className={styles.closeBtn} />
          </div>
        </div>

        {paymentDetail ? (
          <div className={styles.changeable} style={{ paddingBottom: '8px' }}>
            <Grid container alignItems="center" spacing={2}>
              <Grid item xs={6} sm={2} className={styles.inputLabelContainer}>
                <h3 className={styles.inputLabel}>{tCommon('Customer')}</h3>
              </Grid>
              <Grid item xs={6} sm={4}>
                <h3 className={styles.inputLabel}>{customerObj?.name || '-'}</h3>
              </Grid>

              <Grid item xs={6} sm={2} className={styles.inputLabelContainer}>
                <h3 className={styles.inputLabel}>{tCommon('Mobile')}</h3>
              </Grid>
              <Grid item xs={6} sm={4}>
                <h3 className={styles.inputLabel}>{customerObj?.mobile || '-'}</h3>
              </Grid>

              <Grid item xs={6} sm={2} className={styles.inputLabelContainer}>
                <h3 className={styles.inputLabel}>{tCommon('Amount')}</h3>
              </Grid>
              <Grid item xs={6} sm={4}>
                <h3 className={styles.inputLabel}>{pd.amount}</h3>
              </Grid>

              <Grid item xs={6} sm={2} className={styles.inputLabelContainer}>
                <h3 className={styles.inputLabel}>{tCommon('Status')}</h3>
              </Grid>
              <Grid item xs={6} sm={4}>
                <h3 className={styles.inputLabel}>{pd.status}</h3>
              </Grid>

              <Grid item xs={6} sm={2} className={styles.inputLabelContainer}>
                <h3 className={styles.inputLabel}>{tCommon('Date')}</h3>
              </Grid>
              <Grid item xs={6} sm={4}>
                <h3 className={styles.inputLabel}>{DateTimeHelpers.convertDateToIsoDMY(pd.initiated_at)}</h3>
              </Grid>

              <Grid item xs={6} sm={2} className={styles.inputLabelContainer}>
                <h3 className={styles.inputLabel}>{tCommon('Time')}</h3>
              </Grid>
              <Grid item xs={6} sm={4}>
                <h3 className={styles.inputLabel}>{DateTimeHelpers.convertTo12HourTime(pd.initiated_at)}</h3>
              </Grid>
            </Grid>
          </div>
        ) : (
          <Info title={tCommon('Not Found')} content={tOperations('Payment details not found')} />
        )}

        {(loading || items.length > 0) && (
          <DataTable
            className={styles.changeable}
            columns={itemColumns}
            rows={items || []}
            isLoading={loading}
            deactiveRow={false}
          />
        )}
      </div>
    </div>
  );
}
